import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  setDoc,
  limit,
  onSnapshot,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Default-Werte für Treuekarte
const defaultLoyaltyCard = {
  currentRideCount: 0,
  availableDiscounts: 0,
  totalEarnedDiscounts: 0,
  totalRedeemedDiscounts: 0,
  totalSavingsEur: 0,
  lastRideCountedId: null,
  lastUpdated: null
};

// Speicher für die letzte Aktualisierung und Daten
const customersCache = {
  lastFetched: null,
  data: [],
  fetchInProgress: false,
  // Cache-Zeit in Millisekunden (5 Minuten)
  cacheDuration: 5 * 60 * 1000
};

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (options = {}, { getState, rejectWithValue }) => {
    try {
      const { forceRefresh = false, simplified = false } = options;
      const currentPath = window.location.pathname;
      const isCustomersPage = currentPath.startsWith('/kunden');
      
      // Verhindert parallele Anfragen
      if (customersCache.fetchInProgress) {
        console.log('Bereits eine Anfrage in Bearbeitung, warte auf deren Abschluss...');
        return getState().customers.customers;
      }
      
      // Prüfe, ob die Kunden bereits geladen wurden und der Cache gültig ist
      const currentState = getState();
      const cacheValid = customersCache.lastFetched && 
                       (Date.now() - customersCache.lastFetched < customersCache.cacheDuration);
      
      if (!forceRefresh && 
          cacheValid && 
          currentState.customers.customers && 
          currentState.customers.customers.length > 0) {
        console.log('Verwende gecachte Kundendaten (letzte Aktualisierung:', 
                 new Date(customersCache.lastFetched).toLocaleTimeString(), ')');
        return currentState.customers.customers;
      }
      
      console.log('Starte fetchCustomers...', { 
        forceRefresh, 
        simplified, 
        currentPath,
        isCustomersPage
      });
      
      // Flag setzen, dass Anfrage läuft
      customersCache.fetchInProgress = true;
      
      // Kunden sind Benutzer mit isDriver=false
      const customersQuery = query(
        collection(db, 'users'),
        where('isDriver', '==', false),
        orderBy('name', 'asc')
      );
      
      console.log('Führe Customers-Query aus...');
      const querySnapshot = await getDocs(customersQuery);
      console.log('Query-Ergebnis:', querySnapshot.size, 'Kunden gefunden');
      const customers = [];
      
      // Parallele Verarbeitung für bessere Performance
      const customerPromises = querySnapshot.docs.map(async (userDoc) => {
        const userData = userDoc.data();
        const userId = userDoc.id;
        
        // Für Debug-Zwecke die ersten beiden Kunden detailliert loggen
        const detailLogging = customers.length < 2;
        if (detailLogging) {
          console.log('Verarbeite Kunden:', userId);
          console.log('Kundendaten:', userData);
        }
        
        // Grundlegende Daten, die wir immer brauchen
        const customerBase = {
          id: userId,
          isOnline: userData.isOnline || false,
          total_rides: 0, // Wird später berechnet
          lastLogin: userData.lastLogin ? userData.lastLogin.toDate() : null,
          loyaltyCard: userData.loyaltyCard
            ? {
                ...defaultLoyaltyCard,
                ...userData.loyaltyCard,
                lastUpdated: userData.loyaltyCard.lastUpdated
                  ? userData.loyaltyCard.lastUpdated.toDate()
                  : null
              }
            : defaultLoyaltyCard,
          user: {
            id: userId,
            name: userData.name,
            email: userData.email,
            phoneNumber: userData.phoneNumber || '',
            address: userData.address || '',
            profile_picture: userData.profilePic || '',
            isPhoneCustomer: userData.isPhoneCustomer || false
          }
        };
        
        // Bei vereinfachter Ansicht geben wir die Basisdaten zurück
        if (simplified && !isCustomersPage) {
          return customerBase;
        }
        
        // Array für parallele Datenabfragen
        const dataFetchPromises = [];
        
        // Fahrtenzählung für Kundendetailseite
        if (isCustomersPage) {
          const ridesPromise = getDocs(query(
            collection(db, 'rides'),
            where('customerId', '==', userId),
            limit(100) // Limit für bessere Performance
          ))
          .then(ridesSnapshot => {
            return ridesSnapshot.docs.length;
          })
          .catch(error => {
            console.warn(`Fehler beim Zählen der Fahrten:`, error);
            return 0;
          });
          
          dataFetchPromises.push(ridesPromise.then(count => {
            customerBase.total_rides = count;
          }));
        }
        
        // Alle Daten parallel laden und dann den Kunden zurückgeben
        if (dataFetchPromises.length > 0) {
          await Promise.all(dataFetchPromises);
        }
        
        return customerBase;
      });
      
      // Alle Kunden-Promises parallel ausführen
      const processedCustomers = await Promise.all(customerPromises);
      customers.push(...processedCustomers);
      
      console.log('Kundenverarbeitung abgeschlossen, gefundene Kunden:', customers.length);
      
      // Cache aktualisieren
      customersCache.data = customers;
      customersCache.lastFetched = Date.now();
      customersCache.fetchInProgress = false;
      
      return customers;
    } catch (error) {
      // Fehler-Flag zurücksetzen
      customersCache.fetchInProgress = false;
      
      console.error('Error fetching customers:', error);
      // Vollständigen Fehler zurückgeben für bessere Diagnose
      return rejectWithValue({
        message: error.message,
        code: error.code,
        stack: error.stack,
        details: 'Fehler beim Abrufen der Kunden. Bitte prüfen Sie die Konsole für Details.'
      });
    }
  }
);

export const fetchCustomerById = createAsyncThunk(
  'customers/fetchCustomerById',
  async (customerId, { rejectWithValue }) => {
    try {
      // Kunden werden direkt aus users Sammlung abgefragt
      const userDoc = await getDoc(doc(db, 'users', customerId));

      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }

      const userData = userDoc.data();

      // Prüfen, ob es sich wirklich um einen Kunden handelt
      if (userData.isDriver) {
        throw new Error('Benutzer ist kein Kunde');
      }

      // Fahrten zählen
      const ridesQuery = query(
        collection(db, 'rides'),
        where('customerId', '==', customerId)
      );
      const ridesSnapshot = await getDocs(ridesQuery);
      const totalRides = ridesSnapshot.docs.length;

      // Abgeschlossene Fahrten zählen
      const completedRidesQuery = query(
        collection(db, 'rides'),
        where('customerId', '==', customerId),
        where('status', '==', 'completed')
      );
      const completedRidesSnapshot = await getDocs(completedRidesQuery);
      const completedRides = completedRidesSnapshot.docs.length;

      // Treuekarten-Daten laden oder Defaults verwenden
      const loyaltyCard = userData.loyaltyCard
        ? {
            ...defaultLoyaltyCard,
            ...userData.loyaltyCard,
            lastUpdated: userData.loyaltyCard.lastUpdated
              ? userData.loyaltyCard.lastUpdated.toDate()
              : null
          }
        : defaultLoyaltyCard;

      return {
        id: customerId,
        isOnline: userData.isOnline || false,
        lastLogin: userData.lastLogin ? userData.lastLogin.toDate() : null,
        total_rides: totalRides,
        completed_rides: completedRides,
        loyaltyCard,
        user: {
          id: customerId,
          name: userData.name,
          email: userData.email,
          phoneNumber: userData.phoneNumber || '',
          address: userData.address || '',
          profile_picture: userData.profilePic || '',
          isPhoneCustomer: userData.isPhoneCustomer || false
        }
      };
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData, { rejectWithValue, dispatch }) => {
    try {
      const { 
        name, 
        phoneNumber,
        address,
        isPhoneCustomer
      } = customerData;
      
      let uid, email;
      
      if (isPhoneCustomer) {
        // Für Telefonkunden einen speziellen Präfix verwenden
        uid = 'phone_' + Date.now().toString();
        // Dummy E-Mail für Telefonkunden
        email = `phone_${phoneNumber.replace(/\D/g, '')}@telephone.local`;
      } else {
        // Für reguläre Kunden
        uid = customerData.uid || ('customer_' + Date.now().toString());
        email = customerData.email || (name.toLowerCase().replace(/\s+/g, '.') + '@example.com');
      }
      
      // Neuen Benutzer in users-Sammlung erstellen
      const userData = {
        name,
        email,
        phoneNumber,
        address: address || '',
        isDriver: false,
        isOnline: false,
        isPhoneCustomer: isPhoneCustomer || false, // Flag für Telefonkunden
        // Metadaten
        createdAt: Timestamp.now(),
        lastLogin: Timestamp.now(),
        platform: 'web',
        profilePic: ''
      };
      
      // Benutzer in Firestore speichern
      await setDoc(doc(db, 'users', uid), userData);
      
      // Aktualisierte Kundendaten abrufen
      const customer = {
        id: uid,
        isOnline: false,
        lastLogin: new Date(),
        total_rides: 0,
        isPhoneCustomer: isPhoneCustomer || false,
        user: {
          id: uid,
          name: name,
          email: email,
          phoneNumber: phoneNumber || '',
          address: address || '',
          profile_picture: '',
        }
      };
      
      return customer;
    } catch (error) {
      console.error('Error creating customer:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, ...customerData }, { rejectWithValue, dispatch }) => {
    try {
      // Direkte Aktualisierung des Benutzerdokuments
      const userRef = doc(db, 'users', id);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }
      
      const userData = userDoc.data();
      
      // Prüfen, ob es sich wirklich um einen Kunden handelt
      if (userData.isDriver) {
        throw new Error('Benutzer ist kein Kunde');
      }
      
      // Daten für das Benutzerdokument vorbereiten
      const updateData = {};
      
      if (customerData.user) {
        if (customerData.user.name) updateData.name = customerData.user.name;
        if (customerData.user.phoneNumber) updateData.phoneNumber = customerData.user.phoneNumber;
        if (customerData.user.address) updateData.address = customerData.user.address;
        if (customerData.user.profile_picture) updateData.profilePic = customerData.user.profile_picture;
      }
      
      // User-Dokument aktualisieren
      await updateDoc(userRef, {
        ...updateData,
        updated_at: Timestamp.now()
      });
      
      // Aktualisierte Daten abrufen
      return await fetchCustomerById(id)(dispatch);
    } catch (error) {
      console.error('Error updating customer:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id, { rejectWithValue }) => {
    try {
      // Wir markieren den Kunden als deaktiviert, anstatt ihn zu löschen
      // Vollständiges Löschen von Benutzern sollte über Auth-System erfolgen
      const userRef = doc(db, 'users', id);
      
      await updateDoc(userRef, {
        deactivated: true,
        deactivated_at: Timestamp.now()
      });
      
      return id;
    } catch (error) {
      console.error('Error deleting customer:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Treuekarten-Verwaltung: Rabatt manuell hinzufügen
export const addLoyaltyDiscount = createAsyncThunk(
  'customers/addLoyaltyDiscount',
  async ({ customerId, amount = 1 }, { rejectWithValue, dispatch }) => {
    try {
      const userRef = doc(db, 'users', customerId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }

      const userData = userDoc.data();
      const currentLoyaltyCard = userData.loyaltyCard || defaultLoyaltyCard;

      await updateDoc(userRef, {
        'loyaltyCard.availableDiscounts': (currentLoyaltyCard.availableDiscounts || 0) + amount,
        'loyaltyCard.totalEarnedDiscounts': (currentLoyaltyCard.totalEarnedDiscounts || 0) + amount,
        'loyaltyCard.lastUpdated': serverTimestamp()
      });

      // Aktualisierte Kundendaten laden
      dispatch(fetchCustomerById(customerId));

      return { customerId, amount };
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Rabatts:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Treuekarten-Verwaltung: Rabatt manuell entfernen
export const removeLoyaltyDiscount = createAsyncThunk(
  'customers/removeLoyaltyDiscount',
  async ({ customerId, amount = 1 }, { rejectWithValue, dispatch }) => {
    try {
      const userRef = doc(db, 'users', customerId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }

      const userData = userDoc.data();
      const currentLoyaltyCard = userData.loyaltyCard || defaultLoyaltyCard;
      const currentAvailable = currentLoyaltyCard.availableDiscounts || 0;

      if (currentAvailable < amount) {
        throw new Error('Nicht genügend Rabatte verfügbar');
      }

      await updateDoc(userRef, {
        'loyaltyCard.availableDiscounts': currentAvailable - amount,
        'loyaltyCard.lastUpdated': serverTimestamp()
      });

      // Aktualisierte Kundendaten laden
      dispatch(fetchCustomerById(customerId));

      return { customerId, amount };
    } catch (error) {
      console.error('Fehler beim Entfernen des Rabatts:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Treuekarten-Verwaltung: Fortschritt anpassen
export const updateLoyaltyProgress = createAsyncThunk(
  'customers/updateLoyaltyProgress',
  async ({ customerId, newCount }, { rejectWithValue, dispatch }) => {
    try {
      const userRef = doc(db, 'users', customerId);

      await updateDoc(userRef, {
        'loyaltyCard.currentRideCount': newCount,
        'loyaltyCard.lastUpdated': serverTimestamp()
      });

      // Aktualisierte Kundendaten laden
      dispatch(fetchCustomerById(customerId));

      return { customerId, newCount };
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Fortschritts:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Treuekarten-Verwaltung: Zurücksetzen
export const resetLoyaltyCard = createAsyncThunk(
  'customers/resetLoyaltyCard',
  async (customerId, { rejectWithValue, dispatch }) => {
    try {
      const userRef = doc(db, 'users', customerId);

      await updateDoc(userRef, {
        'loyaltyCard': {
          currentRideCount: 0,
          availableDiscounts: 0,
          totalEarnedDiscounts: 0,
          totalRedeemedDiscounts: 0,
          totalSavingsEur: 0,
          lastRideCountedId: null,
          lastUpdated: serverTimestamp()
        }
      });

      // Aktualisierte Kundendaten laden
      dispatch(fetchCustomerById(customerId));

      return customerId;
    } catch (error) {
      console.error('Fehler beim Zurücksetzen der Treuekarte:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Remote-Logout eines Kunden
export const logoutCustomer = createAsyncThunk(
  'customers/logoutCustomer',
  async (customerId, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'users', customerId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Kunde nicht gefunden');
      }
      
      // Remote-Logout-Flags setzen
      await updateDoc(userRef, {
        forceLogout: true,
        forceLogoutTimestamp: serverTimestamp()
      });
      
      return customerId;
    } catch (error) {
      console.error('Fehler beim Remote-Logout des Kunden:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Aktiver Listener-Speicher außerhalb des Reducer, um Cleanup zu ermöglichen
let customersStatusListener = null;

const initialState = {
  customers: [],
  currentCustomer: null,
  isLoading: false,
  error: null
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearCustomersError: (state) => {
      state.error = null;
    },
    clearCurrentCustomer: (state) => {
      state.currentCustomer = null;
    },
    updateCustomerRealtime: (state, action) => {
      const customerData = action.payload;
      const index = state.customers.findIndex(customer => customer.id === customerData.id);
      
      if (index !== -1) {
        // Nur die aktualisierten Felder übernehmen
        state.customers[index] = {
          ...state.customers[index],
          ...customerData
        };
      }
      
      // Auch den aktuellen Kunden aktualisieren, falls es derselbe ist
      if (state.currentCustomer && state.currentCustomer.id === customerData.id) {
        state.currentCustomer = {
          ...state.currentCustomer,
          ...customerData
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all customers
      .addCase(fetchCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch customer by ID
      .addCase(fetchCustomerById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCustomer = action.payload;
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create customer
      .addCase(createCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers.push(action.payload);
        state.currentCustomer = action.payload;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update customer
      .addCase(updateCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.customers.findIndex(customer => customer.id === action.payload.id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
        if (state.currentCustomer && state.currentCustomer.id === action.payload.id) {
          state.currentCustomer = action.payload;
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete customer
      .addCase(deleteCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = state.customers.filter(customer => customer.id !== action.payload);
        if (state.currentCustomer && state.currentCustomer.id === action.payload) {
          state.currentCustomer = null;
        }
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Logout customer
      .addCase(logoutCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(logoutCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optional: Hier könnten wir den Online-Status des Kunden aktualisieren
        const index = state.customers.findIndex(customer => customer.id === action.payload);
        if (index !== -1) {
          state.customers[index].isOnline = false;
        }
      })
      .addCase(logoutCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

// Thunk zum Starten des Echtzeit-Listeners für Kunden-Status
export const startCustomersStatusListener = () => (dispatch) => {
  console.log('Starte Echtzeit-Listener für Kunden-Status...');
  
  // Existierenden Listener entfernen, falls vorhanden
  if (customersStatusListener) {
    console.log('Beende alten Kunden-Listener...');
    customersStatusListener();
    customersStatusListener = null;
  }
  
  // Listener für die Benutzersammlung einrichten (nur Kunden, keine Fahrer)
  const usersQuery = query(
    collection(db, 'users'),
    where('isDriver', '==', false)
  );
  
  customersStatusListener = onSnapshot(
    usersQuery,
    (snapshot) => {
      snapshot.docChanges().forEach(change => {
        const userData = change.doc.data();
        const userId = change.doc.id;
        
        // Nur bei neuen oder geänderten Dokumenten
        if (change.type === 'added' || change.type === 'modified') {
          console.log(`Echtzeit-Update für Kunde ${userId}:`, userData.isOnline);
          
          // Relevante Statusdaten extrahieren und an den Store senden
          dispatch({
            type: 'customers/updateCustomerRealtime',
            payload: {
              id: userId,
              isOnline: userData.isOnline || false,
              lastLogin: userData.lastLogin ? userData.lastLogin.toDate() : null
            }
          });
        }
      });
    },
    (error) => {
      console.error('Fehler im Kunden-Status-Listener:', error);
    }
  );
  
  return () => {
    // Cleanup-Funktion zurückgeben
    if (customersStatusListener) {
      customersStatusListener();
      customersStatusListener = null;
    }
  };
};

// Thunk zum Stoppen des Echtzeit-Listeners
export const stopCustomersStatusListener = () => () => {
  console.log('Stoppe Echtzeit-Listener für Kunden-Status...');
  if (customersStatusListener) {
    customersStatusListener();
    customersStatusListener = null;
  }
};

export const { clearCustomersError, clearCurrentCustomer, updateCustomerRealtime } = customersSlice.actions;
export default customersSlice.reducer;